import { useState, useEffect } from "react";
import {
  MapPin,
  Calendar,
  Wallet,
  Compass,
  Loader,
  Car,
  Hotel,
  UtensilsCrossed,
  Ticket,
  Info,
  AlertTriangle,
  Users,
  Navigation,
  Sparkles,
  Check,
} from "lucide-react";
import API from "../api";

const INDIAN_CITIES = [
  "Mumbai",
  "Delhi",
  "Bangalore",
  "Hyderabad",
  "Ahmedabad",
  "Chennai",
  "Kolkata",
  "Surat",
  "Pune",
  "Jaipur",
  "Lucknow",
  "Kanpur",
  "Nagpur",
  "Indore",
  "Thane",
  "Bhopal",
  "Visakhapatnam",
  "Pimpri-Chinchwad",
  "Patna",
  "Vadodara",
  "Ghaziabad",
  "Ludhiana",
  "Agra",
  "Nashik",
  "Faridabad",
  "Meerut",
  "Rajkot",
  "Kalyan-Dombivli",
  "Vasai-Virar",
  "Varanasi",
  "Srinagar",
  "Aurangabad",
  "Dhanbad",
  "Amritsar",
  "Navi Mumbai",
  "Allahabad",
  "Ranchi",
  "Howrah",
  "Coimbatore",
  "Jabalpur",
  "Gwalior",
  "Vijayawada",
  "Jodhpur",
  "Madurai",
  "Raipur",
  "Kota",
  "Guwahati",
  "Chandigarh",
  "Solapur",
  "Hubli-Dharwad",
];

const VIBES = [
  { value: "Adventure", label: "Adventure", emoji: "🏔️" },
  { value: "Spiritual", label: "Spiritual", emoji: "🛕" },
  { value: "Nature & Wildlife", label: "Nature & Wildlife", emoji: "🌿" },
  { value: "Offbeat", label: "Offbeat", emoji: "🗺️" },
  { value: "Family", label: "Family", emoji: "👨‍👩‍👧" },
  { value: "Photography", label: "Photography", emoji: "📷" },
];

const BUDGET_OPTIONS = [
  {
    value: "Backpacker (1200-1800)",
    label: "BACKPACKER",
    desc: "Hostels, buses, dhabas",
    price: "₹1,200 - ₹1,800/day",
    icon: Wallet,
  },
  {
    value: "Budget (2500-3500)",
    label: "BUDGET",
    desc: "Budget hotels, shared cabs",
    price: "₹2,500 - ₹3,500/day",
    icon: Wallet,
  },
  {
    value: "Mid Range (5000-7000)",
    label: "MID RANGE",
    desc: "2-star hotels, private cabs",
    price: "₹5,000 - ₹7,000/day",
    icon: Wallet,
  },
  {
    value: "Comfort (9000-14000)",
    label: "COMFORT",
    desc: "3-star hotels, AC travel",
    price: "₹9,000 - ₹14,000/day",
    icon: Wallet,
  },
  {
    value: "Luxury (18000+)",
    label: "LUXURY",
    desc: "Resorts, private vehicles",
    price: "₹18,000+/day",
    icon: Wallet,
  },
];

const TRAVEL_STYLES = [
  { value: "Solo traveler", label: "Solo traveler", emoji: "🧍" },
  { value: "Couple", label: "Couple", emoji: "👫" },
  { value: "Friends group", label: "Friends group", emoji: "🧑‍🤝‍🧑" },
  { value: "Family with kids", label: "Family with kids", emoji: "👨‍👩‍👧‍👦" },
  {
    value: "Senior citizens group",
    label: "Senior citizens group",
    emoji: "👴👵",
  },
];

const TRANSPORTS = [
  {
    value: "Hatchback/Sedan",
    label: "Hatchback / Sedan",
    desc: "Comfortable for 2-3 people",
    rate: "₹12-15/km",
    emoji: "🚕",
  },
  {
    value: "SUV/Innova",
    label: "SUV / Innova",
    desc: "Best for 4-6 people & hills",
    rate: "₹18-22/km",
    emoji: "🚙",
  },
  {
    value: "Shared Cab/Bus",
    label: "Shared Cab / Bus",
    desc: "Budget friendly",
    rate: "₹5-8/km",
    emoji: "🚌",
  },
];

const REGIONS = [
  {
    value: "Garhwal",
    label: "Garhwal",
    desc: "Rishikesh, Haridwar, Kedarnath, Badrinath, Auli, Chopta",
  },
  {
    value: "Kumaon",
    label: "Kumaon",
    desc: "Nainital, Mussoorie, Corbett, Ranikhet, Munsiyari",
  },
  {
    value: "Both",
    label: "Both (Garhwal + Kumaon)",
    desc: "Requires at least 7 days for a good experience",
  },
];

const DURATIONS = [1, 2, 3, 4, 5, 7, 10, 14];

const MONTHS = [
  { value: "January", label: "Jan", emoji: "❄️" },
  { value: "February", label: "Feb", emoji: "⛄" },
  { value: "March", label: "Mar", emoji: "🌸" },
  { value: "April", label: "Apr", emoji: "🌼" },
  { value: "May", label: "May", emoji: "☀️" },
  { value: "June", label: "Jun", emoji: "🏕️" },
  { value: "July", label: "Jul", emoji: "🌧️" },
  { value: "August", label: "Aug", emoji: "☔" },
  { value: "September", label: "Sep", emoji: "🌿" },
  { value: "October", label: "Oct", emoji: "🍁" },
  { value: "November", label: "Nov", emoji: "🪔" },
  { value: "December", label: "Dec", emoji: "🎄" },
];

// Shared style for step card container
const stepCard = {
  background: "white",
  borderRadius: 16,
  border: "1px solid rgba(10,42,10,0.08)",
  padding: "24px 20px",
};

// Shared style for primary action button
const primaryBtn = (disabled) => ({
  background: disabled ? "#9ca3af" : "#1D9E75",
  color: "white",
  borderRadius: 999,
  width: "100%",
  padding: "14px",
  fontWeight: 600,
  fontSize: 15,
  border: "none",
  cursor: disabled ? "not-allowed" : "pointer",
  transition: "background 0.2s",
});

const backBtn = {
  padding: "12px 20px",
  border: "2px solid #1a3a1a",
  borderRadius: 999,
  fontWeight: 600,
  fontSize: 14,
  color: "#1a3a1a",
  background: "transparent",
  cursor: "pointer",
  flexShrink: 0,
};

// Option pill default/selected
const optionPill = (selected) => ({
  background: selected ? "#1a3a1a" : "#e8f5e9",
  border: selected ? "1px solid #1a3a1a" : "1px solid rgba(29,158,117,0.15)",
  color: selected ? "white" : "#0a3a1a",
  borderRadius: 999,
  padding: "8px 16px",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  transition: "all 0.2s",
});

// Option card (text-left block)
const optionCard = (selected) => ({
  background: selected ? "#e8f5e9" : "white",
  border: selected ? "2px solid #1D9E75" : "2px solid rgba(0,0,0,0.08)",
  borderRadius: 14,
  padding: "14px 16px",
  textAlign: "left",
  cursor: "pointer",
  transition: "all 0.2s",
  width: "100%",
});

export default function TripPlanner() {
  const [step, setStep] = useState(1);
  const [origins, setOrigins] = useState([]);
  const [customOrigin, setCustomOrigin] = useState("");

  const [form, setForm] = useState({
    origin: "",
    month: "",
    region: "",
    days: 3,
    travel_style: "",
    vibe: [],
    budget: "",
    transport: "",
  });

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    API.get("/trip-planner/origins")
      .then((res) => {
        setOrigins(res.data || []);
      })
      .catch((err) => {
        console.error("Failed to load origins", err);
        setOrigins([
          {
            city: "Delhi NCR",
            type: "road",
            distance: 230,
            time: "5 hrs drive",
            entry: "Haridwar/Rishikesh",
          },
          {
            city: "Mumbai",
            type: "flight",
            distance: 0,
            time: "Fly to Dehradun (~2hrs)",
            entry: "Dehradun",
          },
          {
            city: "Other",
            type: "other",
            distance: 0,
            time: "Depends",
            entry: "Depends",
          },
        ]);
      });
  }, []);

  const handleNext = () => setStep((s) => s + 1);
  const handlePrev = () => setStep((s) => Math.max(1, s - 1));

  const toggleVibe = (v) =>
    setForm((f) => ({
      ...f,
      vibe: f.vibe.includes(v) ? f.vibe.filter((x) => x !== v) : [...f.vibe, v],
    }));

  const getDurationWarning = (days) => {
    if (days <= 2)
      return "Best for: Rishikesh/Haridwar only. Cannot do hill stations properly.";
    if (days === 3)
      return "Best for: Rishikesh + 1 nearby place (Chopta OR Devprayag). Cannot cover multiple zones.";
    if (days === 4 || days === 5)
      return "Can cover Rishikesh + Auli + Chopta (Garhwal circuit).";
    if (days >= 7) return "Can combine Garhwal + Kumaon OR do Char Dham yatra.";
    return "";
  };

  const getMonthWarning = (month) => {
    if (["May", "June"].includes(month))
      return "Peak summer season. Very crowded, book everything in advance. Perfect for high-altitude treks.";
    if (["July", "August"].includes(month))
      return "Monsoon season. High risk of landslides and road blocks. Travel with flexible plans.";
    if (["October", "November"].includes(month))
      return "BEST TIME! Clear skies, pleasant weather, great views of the Himalayas.";
    if (["December", "January", "February"].includes(month))
      return "Winter season. Heavy snowfall in higher reaches. Many roads to high altitudes might be closed.";
    return "Shoulder season. Good weather with fewer crowds.";
  };

  const generate = async () => {
    setError("");
    setLoading(true);
    setResult(null);
    try {
      const finalOrigin = form.origin === "Other" ? customOrigin : form.origin;
      const reqData = { ...form, origin: finalOrigin };

      const res = await API.post("/trip-planner/generate", reqData);
      setResult(res.data);
      setStep(9);
    } catch (e) {
      setError(
        e.response?.data?.detail || "Something went wrong generating your trip",
      );
    } finally {
      setLoading(false);
    }
  };

  const currentStepIndex = step - 1;
  const totalSteps = 8;

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div style={stepCard}>
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    background: "#1D9E75",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  1
                </div>
                <h2
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#0a2a0a",
                    margin: 0,
                  }}
                >
                  Starting Point
                </h2>
              </div>
              <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
                Where are you beginning your journey?
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div>
                <h3
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#1D9E75",
                    marginBottom: 10,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  North India (By Road)
                </h3>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {origins
                    .filter((o) => o.type === "road")
                    .map((o) => (
                      <button
                        key={o.city}
                        onClick={() =>
                          setForm((f) => ({ ...f, origin: o.city }))
                        }
                        style={optionCard(form.origin === o.city)}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            color: "#0a2a0a",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: 14,
                          }}
                        >
                          <MapPin size={14} style={{ color: "#1D9E75" }} />{" "}
                          {o.city}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "#6b7280",
                            marginTop: 4,
                          }}
                        >
                          From {o.city}: {o.distance}km / {o.time} to {o.entry}
                        </div>
                      </button>
                    ))}
                </div>
              </div>

              <div>
                <h3
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#1D9E75",
                    marginBottom: 10,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  Nearby By Flight
                </h3>
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {origins
                    .filter((o) => o.type === "flight")
                    .map((o) => (
                      <button
                        key={o.city}
                        onClick={() =>
                          setForm((f) => ({ ...f, origin: o.city }))
                        }
                        style={optionCard(form.origin === o.city)}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            color: "#0a2a0a",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: 14,
                          }}
                        >
                          <MapPin size={14} style={{ color: "#1D9E75" }} />{" "}
                          {o.city}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "#6b7280",
                            marginTop: 4,
                          }}
                        >
                          {o.time}, then transport to destination
                        </div>
                      </button>
                    ))}
                </div>
              </div>

              <div>
                <h3
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#1D9E75",
                    marginBottom: 10,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  Other Location
                </h3>
                {origins
                  .filter((o) => o.type === "other")
                  .map((o) => (
                    <button
                      key={o.city}
                      onClick={() => setForm((f) => ({ ...f, origin: o.city }))}
                      style={{
                        ...optionCard(form.origin === o.city),
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#0a2a0a",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          fontSize: 14,
                        }}
                      >
                        <MapPin size={14} style={{ color: "#1D9E75" }} />{" "}
                        {o.city}
                      </div>
                    </button>
                  ))}
                {form.origin === "Other" && (
                  <div style={{ marginTop: 10 }}>
                    <input
                      type="text"
                      placeholder="Enter your city"
                      value={customOrigin}
                      onChange={(e) => setCustomOrigin(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        border: "1px solid rgba(0,0,0,0.1)",
                        borderRadius: 12,
                        fontSize: 14,
                        color: "#0a2a0a",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                    {customOrigin &&
                      INDIAN_CITIES.filter(
                        (c) =>
                          c
                            .toLowerCase()
                            .includes(customOrigin.toLowerCase()) &&
                          c.toLowerCase() !== customOrigin.toLowerCase(),
                      ).length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 8,
                            marginTop: 10,
                          }}
                        >
                          {INDIAN_CITIES.filter(
                            (c) =>
                              c
                                .toLowerCase()
                                .includes(customOrigin.toLowerCase()) &&
                              c.toLowerCase() !== customOrigin.toLowerCase(),
                          )
                            .slice(0, 5)
                            .map((city) => (
                              <button
                                key={city}
                                onClick={() => setCustomOrigin(city)}
                                style={{
                                  padding: "6px 12px",
                                  background: "#e8f5e9",
                                  border: "1px solid rgba(29,158,117,0.2)",
                                  borderRadius: 999,
                                  fontSize: 12,
                                  color: "#0a3a1a",
                                  cursor: "pointer",
                                }}
                              >
                                {city}
                              </button>
                            ))}
                        </div>
                      )}
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginTop: 24 }}>
              <button
                disabled={
                  !form.origin || (form.origin === "Other" && !customOrigin)
                }
                onClick={handleNext}
                style={primaryBtn(
                  !form.origin || (form.origin === "Other" && !customOrigin),
                )}
              >
                Continue
              </button>
            </div>
          </div>
        );

      case 2:
        return (
          <div style={stepCard}>
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    background: "#1D9E75",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  2
                </div>
                <h2
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#0a2a0a",
                    margin: 0,
                  }}
                >
                  Travel Month
                </h2>
              </div>
              <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
                When are you planning to visit?
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 10,
              }}
            >
              {MONTHS.map((m) => (
                <button
                  key={m.value}
                  onClick={() => setForm((f) => ({ ...f, month: m.value }))}
                  style={{
                    ...optionCard(form.month === m.value),
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: "14px 8px",
                    gap: 6,
                  }}
                >
                  <span style={{ fontSize: 24 }}>{m.emoji}</span>
                  <span
                    style={{ fontWeight: 600, fontSize: 12, color: "#0a2a0a" }}
                  >
                    {m.label}
                  </span>
                </button>
              ))}
            </div>

            {form.month && (
              <div
                style={{
                  marginTop: 16,
                  padding: "12px 16px",
                  borderRadius: 12,
                  background: "#e8f5e9",
                  border: "1px solid rgba(29,158,117,0.2)",
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                }}
              >
                <Info
                  size={16}
                  style={{ color: "#1D9E75", flexShrink: 0, marginTop: 2 }}
                />
                <div>
                  <p
                    style={{
                      fontWeight: 600,
                      color: "#0a2a0a",
                      fontSize: 13,
                      margin: 0,
                      marginBottom: 4,
                    }}
                  >
                    {form.month} in Uttarakhand
                  </p>
                  <p
                    style={{
                      fontSize: 12,
                      color: "#374151",
                      lineHeight: 1.5,
                      margin: 0,
                    }}
                  >
                    {getMonthWarning(form.month)}
                  </p>
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={handlePrev} style={backBtn}>
                Back
              </button>
              <button
                disabled={!form.month}
                onClick={handleNext}
                style={{ ...primaryBtn(!form.month), flex: 1, width: "auto" }}
              >
                Continue
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div style={stepCard}>
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    background: "#1D9E75",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  3
                </div>
                <h2
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#0a2a0a",
                    margin: 0,
                  }}
                >
                  Region Focus
                </h2>
              </div>
              <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
                Which part of Uttarakhand do you want to explore?
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {REGIONS.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setForm((f) => ({ ...f, region: r.value }))}
                  style={optionCard(form.region === r.value)}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      color: "#0a2a0a",
                      fontSize: 15,
                      marginBottom: 4,
                    }}
                  >
                    {r.label}
                  </div>
                  <div
                    style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}
                  >
                    {r.desc}
                  </div>
                </button>
              ))}
            </div>
            {form.region === "Both" && form.days < 7 && (
              <div
                style={{
                  marginTop: 14,
                  padding: "12px 16px",
                  background: "#fffbeb",
                  border: "1px solid #fcd34d",
                  borderRadius: 12,
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                }}
              >
                <AlertTriangle
                  size={16}
                  style={{ color: "#d97706", flexShrink: 0, marginTop: 2 }}
                />
                <p
                  style={{
                    fontSize: 12,
                    color: "#92400e",
                    margin: 0,
                    lineHeight: 1.5,
                  }}
                >
                  Warning: Covering both regions requires at least 7 days for a
                  comfortable trip.
                </p>
              </div>
            )}
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={handlePrev} style={backBtn}>
                Back
              </button>
              <button
                disabled={!form.region}
                onClick={handleNext}
                style={{ ...primaryBtn(!form.region), flex: 1, width: "auto" }}
              >
                Continue
              </button>
            </div>
          </div>
        );

      case 4:
        return (
          <div style={stepCard}>
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    background: "#1D9E75",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  4
                </div>
                <h2
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#0a2a0a",
                    margin: 0,
                  }}
                >
                  Trip Duration
                </h2>
              </div>
              <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
                How many days will you be traveling?
              </p>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {DURATIONS.map((d) => (
                <button
                  key={d}
                  onClick={() => setForm((f) => ({ ...f, days: d }))}
                  style={optionPill(form.days === d)}
                >
                  {d} {d === 1 ? "Day" : "Days"}
                </button>
              ))}
            </div>
            <div
              style={{
                marginTop: 16,
                padding: "12px 16px",
                background: "rgba(0,0,0,0.04)",
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: 12,
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              <Info
                size={16}
                style={{ color: "#6b7280", flexShrink: 0, marginTop: 2 }}
              />
              <p
                style={{
                  fontSize: 12,
                  color: "#374151",
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                {getDurationWarning(form.days)}
              </p>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={handlePrev} style={backBtn}>
                Back
              </button>
              <button
                onClick={handleNext}
                style={{ ...primaryBtn(false), flex: 1, width: "auto" }}
              >
                Continue
              </button>
            </div>
          </div>
        );

      case 5:
        return (
          <div style={stepCard}>
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    background: "#1D9E75",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  5
                </div>
                <h2
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#0a2a0a",
                    margin: 0,
                  }}
                >
                  Travel Companions
                </h2>
              </div>
              <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
                Who are you traveling with?
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {TRAVEL_STYLES.map((ts) => (
                <button
                  key={ts.value}
                  onClick={() =>
                    setForm((f) => ({ ...f, travel_style: ts.value }))
                  }
                  style={{
                    ...optionCard(form.travel_style === ts.value),
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                  }}
                >
                  <span style={{ fontSize: 28 }}>{ts.emoji}</span>
                  <span
                    style={{ fontWeight: 600, color: "#0a2a0a", fontSize: 15 }}
                  >
                    {ts.label}
                  </span>
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={handlePrev} style={backBtn}>
                Back
              </button>
              <button
                disabled={!form.travel_style}
                onClick={handleNext}
                style={{
                  ...primaryBtn(!form.travel_style),
                  flex: 1,
                  width: "auto",
                }}
              >
                Continue
              </button>
            </div>
          </div>
        );

      case 6:
        return (
          <div style={stepCard}>
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    background: "#1D9E75",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  6
                </div>
                <h2
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#0a2a0a",
                    margin: 0,
                  }}
                >
                  Travel Vibe
                </h2>
              </div>
              <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
                Select all that apply for your ideal trip
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 10,
              }}
            >
              {VIBES.map((v) => (
                <button
                  key={v.value}
                  onClick={() => toggleVibe(v.value)}
                  style={{
                    ...optionCard(form.vibe.includes(v.value)),
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: "18px 12px",
                    gap: 8,
                  }}
                >
                  <span style={{ fontSize: 32 }}>{v.emoji}</span>
                  <span
                    style={{
                      fontWeight: 600,
                      color: "#0a2a0a",
                      textAlign: "center",
                      fontSize: 13,
                    }}
                  >
                    {v.label}
                  </span>
                </button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={handlePrev} style={backBtn}>
                Back
              </button>
              <button
                disabled={form.vibe.length === 0}
                onClick={handleNext}
                style={{
                  ...primaryBtn(form.vibe.length === 0),
                  flex: 1,
                  width: "auto",
                }}
              >
                Continue
              </button>
            </div>
          </div>
        );

      case 7:
        return (
          <div style={stepCard}>
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    background: "#1D9E75",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  7
                </div>
                <h2
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#0a2a0a",
                    margin: 0,
                  }}
                >
                  Budget Level
                </h2>
              </div>
              <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
                What's your expected budget per day?
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {BUDGET_OPTIONS.map((b) => {
                const Icon = b.icon;
                return (
                  <button
                    key={b.value}
                    onClick={() => setForm((f) => ({ ...f, budget: b.value }))}
                    style={optionCard(form.budget === b.value)}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: 4,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <div
                          style={{
                            padding: "6px",
                            borderRadius: 8,
                            background:
                              form.budget === b.value
                                ? "#1D9E75"
                                : "rgba(0,0,0,0.06)",
                            color:
                              form.budget === b.value ? "white" : "#6b7280",
                          }}
                        >
                          <Icon size={14} />
                        </div>
                        <span
                          style={{
                            fontWeight: 700,
                            color: "#0a2a0a",
                            fontSize: 13,
                          }}
                        >
                          {b.label}
                        </span>
                      </div>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#1D9E75",
                        }}
                      >
                        {b.price}
                      </span>
                    </div>
                    <div
                      style={{ fontSize: 12, color: "#6b7280", marginLeft: 36 }}
                    >
                      {b.desc}
                    </div>
                  </button>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={handlePrev} style={backBtn}>
                Back
              </button>
              <button
                disabled={!form.budget}
                onClick={handleNext}
                style={{ ...primaryBtn(!form.budget), flex: 1, width: "auto" }}
              >
                Continue
              </button>
            </div>
          </div>
        );

      case 8:
        return (
          <div style={stepCard}>
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    background: "#1D9E75",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: 700,
                    fontSize: 13,
                  }}
                >
                  8
                </div>
                <h2
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#0a2a0a",
                    margin: 0,
                  }}
                >
                  Transport Mode
                </h2>
              </div>
              <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
                How will you travel around Uttarakhand?
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {TRANSPORTS.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setForm((f) => ({ ...f, transport: t.value }))}
                  style={{
                    ...optionCard(form.transport === t.value),
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 14 }}
                  >
                    <span style={{ fontSize: 28 }}>{t.emoji}</span>
                    <div style={{ textAlign: "left" }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#0a2a0a",
                          fontSize: 15,
                        }}
                      >
                        {t.label}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        {t.desc}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{ fontWeight: 700, color: "#1D9E75", fontSize: 13 }}
                  >
                    {t.rate}
                  </div>
                </button>
              ))}
            </div>
            {error && (
              <div
                style={{
                  marginTop: 14,
                  padding: "12px 16px",
                  background: "#fef2f2",
                  color: "#dc2626",
                  fontSize: 13,
                  borderRadius: 10,
                  border: "1px solid #fecaca",
                }}
              >
                {error}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={handlePrev} disabled={loading} style={backBtn}>
                Back
              </button>
              <button
                disabled={!form.transport || loading}
                onClick={generate}
                style={{
                  ...primaryBtn(!form.transport || loading),
                  flex: 1,
                  width: "auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                {loading ? (
                  <>
                    <Loader
                      size={16}
                      style={{ animation: "spin 1s linear infinite" }}
                    />{" "}
                    Generating Magic...
                  </>
                ) : (
                  <>
                    <Sparkles size={16} /> Generate Itinerary
                  </>
                )}
              </button>
            </div>
          </div>
        );

      case 9:
        if (!result) return null;
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Overview Box */}
            <div
              style={{
                background: "white",
                border: "1px solid rgba(10,42,10,0.08)",
                borderRadius: 20,
                padding: "24px 20px",
              }}
            >
              <h2
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: "#0a2a0a",
                  marginBottom: 10,
                }}
              >
                Your Epic {form.days}-Day Journey
              </h2>
              <p
                style={{
                  fontSize: 14,
                  color: "#374151",
                  lineHeight: 1.7,
                  marginBottom: 16,
                }}
              >
                {result.trip_overview}
              </p>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {[
                  { icon: MapPin, text: `${form.origin} → ${form.region}` },
                  { icon: Calendar, text: form.month },
                  { icon: Users, text: form.travel_style },
                  { icon: Car, text: form.transport },
                ].map((tag, i) => {
                  const Icon = tag.icon;
                  return (
                    <span
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        background: "#e8f5e9",
                        border: "1px solid rgba(29,158,117,0.2)",
                        color: "#0a3a1a",
                        borderRadius: 999,
                        padding: "6px 12px",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      <Icon size={12} /> {tag.text}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Getting There */}
            {result.getting_there && (
              <div
                style={{
                  background: "#f8fafc",
                  border: "1px solid rgba(0,0,0,0.06)",
                  borderRadius: 20,
                  padding: "24px 20px",
                }}
              >
                <h3
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#0a2a0a",
                    marginBottom: 14,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <Car size={20} style={{ color: "#1D9E75" }} /> Getting There
                </h3>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{ fontSize: 14, color: "#374151", fontWeight: 600 }}
                  >
                    Distance:{" "}
                    <span style={{ color: "#1D9E75" }}>
                      {result.getting_there.distance}
                    </span>
                  </div>
                  <div
                    style={{ fontSize: 14, color: "#374151", fontWeight: 600 }}
                  >
                    Est. Time:{" "}
                    <span style={{ color: "#1D9E75" }}>
                      {result.getting_there.time}
                    </span>
                  </div>
                </div>
                {result.getting_there.options &&
                  result.getting_there.options.length > 0 && (
                    <ul
                      style={{
                        margin: 0,
                        padding: 0,
                        listStyle: "none",
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {result.getting_there.options.map((opt, i) => (
                        <li
                          key={i}
                          style={{
                            fontSize: 13,
                            color: "#4b5563",
                            display: "flex",
                            gap: 8,
                            alignItems: "flex-start",
                            lineHeight: 1.5,
                          }}
                        >
                          <span style={{ color: "#1D9E75", marginTop: 2 }}>
                            •
                          </span>{" "}
                          {opt}
                        </li>
                      ))}
                    </ul>
                  )}
              </div>
            )}

            {/* Cost Disclaimer */}
            <div
              style={{
                background: "#fff3e0",
                borderLeft: "3px solid #e07b38",
                padding: "12px 16px",
                borderRadius: "0 8px 8px 0",
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
              }}
            >
              <AlertTriangle
                size={18}
                style={{ color: "#e07b38", flexShrink: 0, marginTop: 2 }}
              />
              <p
                style={{
                  fontSize: 13,
                  color: "#b75b1d",
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                <strong>Cost estimates are approximate:</strong> Actual prices
                may vary based on booking time, exact accommodation, and
                seasonal demand. Transport costs depend on local union rates.
              </p>
            </div>

            {/* Cost Breakdown */}
            {result.budget_summary && (
              <div
                style={{
                  background: "white",
                  border: "1px solid rgba(10,42,10,0.08)",
                  borderRadius: 20,
                  padding: "24px 20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 20,
                    paddingBottom: 16,
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                  }}
                >
                  <div>
                    <p
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        marginBottom: 4,
                      }}
                    >
                      Est. Trip Cost
                    </p>
                    <p
                      style={{
                        fontSize: 28,
                        fontWeight: 700,
                        color: "#0a2a0a",
                        margin: 0,
                      }}
                    >
                      ₹{result.budget_summary.total?.toLocaleString()}
                    </p>
                  </div>
                  <div
                    style={{
                      background: "#e8f5e9",
                      borderRadius: 12,
                      padding: "10px 16px",
                      textAlign: "right",
                    }}
                  >
                    <p
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#6b7280",
                        textTransform: "uppercase",
                        marginBottom: 2,
                      }}
                    >
                      Per person approx
                    </p>
                    <p
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: "#1D9E75",
                        margin: 0,
                      }}
                    >
                      ₹
                      {Math.round(
                        (result.budget_summary.total || 0) /
                          (form.travel_style.includes("Solo") ? 1 : 2),
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 12,
                  }}
                >
                  {[
                    {
                      label: "Travel",
                      val: result.budget_summary.travel,
                      icon: Car,
                      bg: "#eff6ff",
                      color: "#3b82f6",
                    },
                    {
                      label: "Hotels",
                      val: result.budget_summary.hotel,
                      icon: Hotel,
                      bg: "#f5f3ff",
                      color: "#8b5cf6",
                    },
                    {
                      label: "Food",
                      val: result.budget_summary.food,
                      icon: UtensilsCrossed,
                      bg: "#ecfdf5",
                      color: "#10b981",
                    },
                    {
                      label: "Activities",
                      val: result.budget_summary.activities,
                      icon: Ticket,
                      bg: "#fffbeb",
                      color: "#f59e0b",
                    },
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={item.label}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          textAlign: "center",
                          padding: "14px",
                          borderRadius: 12,
                          border: "1px solid rgba(0,0,0,0.06)",
                        }}
                      >
                        <div
                          style={{
                            padding: "10px",
                            borderRadius: 10,
                            background: item.bg,
                            color: item.color,
                            marginBottom: 8,
                          }}
                        >
                          <Icon size={20} />
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#6b7280",
                            marginBottom: 4,
                          }}
                        >
                          {item.label}
                        </div>
                        <div
                          style={{
                            fontSize: 15,
                            fontWeight: 700,
                            color: "#0a2a0a",
                          }}
                        >
                          ₹{item.val?.toLocaleString()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Itinerary Breakdown */}
            <div>
              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#0a2a0a",
                  marginBottom: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Navigation size={20} style={{ color: "#1D9E75" }} /> Itinerary
                Breakdown
              </h3>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                {result.days?.map((day, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: "white",
                      border: "1px solid rgba(0,0,0,0.06)",
                      borderRadius: 16,
                      padding: "18px",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 4,
                        background:
                          "linear-gradient(to bottom, #1D9E75, #1a3a1a)",
                      }}
                    />
                    <div style={{ paddingLeft: 12 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: 12,
                          flexWrap: "wrap",
                          gap: 8,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          <div
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 10,
                              background: "#e8f5e9",
                              color: "#1a3a1a",
                              fontWeight: 700,
                              fontSize: 14,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: "1px solid rgba(29,158,117,0.2)",
                            }}
                          >
                            {day.day}
                          </div>
                          <h4
                            style={{
                              fontWeight: 700,
                              fontSize: 15,
                              color: "#0a2a0a",
                              margin: 0,
                            }}
                          >
                            {day.title}
                          </h4>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            background: "#f3f4f6",
                            padding: "6px 12px",
                            borderRadius: 8,
                            fontSize: 12,
                            color: "#6b7280",
                            fontWeight: 600,
                          }}
                        >
                          <span>
                            <Car
                              size={12}
                              style={{ display: "inline", marginRight: 4 }}
                            />
                            {day.distance_km} km
                          </span>
                          <span>|</span>
                          <span>⏱️ {day.time_hours} hrs</span>
                        </div>
                      </div>
                      <ul
                        style={{
                          margin: 0,
                          padding: 0,
                          listStyle: "none",
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        {day.activities?.map((act, i) => (
                          <li
                            key={i}
                            style={{
                              display: "flex",
                              gap: 10,
                              color: "#374151",
                              fontSize: 13,
                              lineHeight: 1.5,
                            }}
                          >
                            <span
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                background: "#1D9E75",
                                flexShrink: 0,
                                marginTop: 6,
                              }}
                            />
                            {act}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Cost Disclaimer Bottom */}
            <div
              style={{
                background: "#fff3e0",
                borderLeft: "3px solid #e07b38",
                padding: "12px 16px",
                borderRadius: "0 8px 8px 0",
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
              }}
            >
              <AlertTriangle
                size={18}
                style={{ color: "#e07b38", flexShrink: 0, marginTop: 2 }}
              />
              <p
                style={{
                  fontSize: 13,
                  color: "#b75b1d",
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                <strong>Cost estimates are approximate:</strong> Actual prices
                may vary based on booking time, exact accommodation, and
                seasonal demand.
              </p>
            </div>

            {/* Hotel Recommendations */}
            {result.hotel_recommendations?.length > 0 && (
              <div>
                <h3
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#0a2a0a",
                    marginBottom: 14,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <Hotel size={20} style={{ color: "#1D9E75" }} /> Stay
                  Recommendations
                </h3>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(200px, 1fr))",
                    gap: 12,
                  }}
                >
                  {result.hotel_recommendations.map((hotel, idx) => (
                    <div
                      key={idx}
                      style={{
                        background: "white",
                        border: "1px solid rgba(0,0,0,0.06)",
                        padding: "14px 16px",
                        borderRadius: 14,
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <p
                          style={{
                            fontWeight: 700,
                            color: "#0a2a0a",
                            fontSize: 14,
                            margin: 0,
                          }}
                        >
                          {hotel.area}
                        </p>
                        <span
                          style={{
                            fontSize: 10,
                            padding: "3px 8px",
                            borderRadius: 6,
                            background: "#e8f5e9",
                            color: "#1a3a1a",
                            fontWeight: 700,
                            textTransform: "uppercase",
                          }}
                        >
                          {hotel.type}
                        </span>
                      </div>
                      <p
                        style={{
                          fontSize: 12,
                          color: "#6b7280",
                          background: "#f3f4f6",
                          borderRadius: 8,
                          padding: "6px 10px",
                          margin: 0,
                          textAlign: "center",
                        }}
                      >
                        {hotel.price_range}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Warnings & Notes */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 14,
              }}
            >
              {result.seasonal_warning && (
                <div
                  style={{
                    background: "#fffbeb",
                    border: "1px solid #fcd34d",
                    padding: "16px",
                    borderRadius: 16,
                  }}
                >
                  <h4
                    style={{
                      fontWeight: 700,
                      color: "#92400e",
                      marginBottom: 8,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 14,
                    }}
                  >
                    <AlertTriangle size={16} /> Seasonal Warning
                  </h4>
                  <p
                    style={{
                      fontSize: 13,
                      color: "#b45309",
                      lineHeight: 1.6,
                      margin: 0,
                    }}
                  >
                    {result.seasonal_warning}
                  </p>
                </div>
              )}
              {result.important_notes?.length > 0 && (
                <div
                  style={{
                    background: "#f0fdf4",
                    border: "1px solid rgba(29,158,117,0.2)",
                    padding: "16px",
                    borderRadius: 16,
                  }}
                >
                  <h4
                    style={{
                      fontWeight: 700,
                      color: "#1a3a1a",
                      marginBottom: 8,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 14,
                    }}
                  >
                    <Info size={16} /> Important Notes
                  </h4>
                  <ul
                    style={{
                      margin: 0,
                      padding: 0,
                      listStyle: "none",
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                    }}
                  >
                    {result.important_notes.map((n, i) => (
                      <li
                        key={i}
                        style={{
                          fontSize: 13,
                          color: "#1a3a1a",
                          display: "flex",
                          gap: 8,
                          alignItems: "flex-start",
                          lineHeight: 1.5,
                        }}
                      >
                        <span style={{ color: "#1D9E75", marginTop: 2 }}>
                          •
                        </span>{" "}
                        {n}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.booking_tip && (
                <div
                  style={{
                    background: "#eff6ff",
                    border: "1px solid rgba(59,130,246,0.2)",
                    padding: "16px",
                    borderRadius: 16,
                  }}
                >
                  <h4
                    style={{
                      fontWeight: 700,
                      color: "#1e3a8a",
                      marginBottom: 8,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 14,
                    }}
                  >
                    <Check size={16} /> Booking Tip
                  </h4>
                  <p
                    style={{
                      fontSize: 13,
                      color: "#1e3a8a",
                      lineHeight: 1.6,
                      margin: 0,
                    }}
                  >
                    {result.booking_tip}
                  </p>
                </div>
              )}
            </div>

            <div
              style={{
                paddingTop: 16,
                textAlign: "center",
                borderTop: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                justifyContent: "center",
                gap: 12,
              }}
            >
              <button
                onClick={() => setStep(1)}
                style={{
                  ...primaryBtn(false),
                  width: "auto",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "12px 28px",
                }}
              >
                <Compass size={16} /> Plan another trip
              </button>
              <button
                onClick={generate}
                style={{
                  background: "white",
                  color: "#1a3a1a",
                  border: "2px solid #1D9E75",
                  borderRadius: 999,
                  fontWeight: 600,
                  fontSize: 15,
                  padding: "12px 28px",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                Not happy? Regenerate ↺
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      style={{ minHeight: "100vh", background: "#f0f4f0", paddingBottom: 80 }}
    >
      {/* Hero Header */}
      <div style={{ background: "#0a2a0a", padding: "48px 20px 36px" }}>
        <div style={{ maxWidth: 600, margin: "0 auto" }}>
          <h1
            style={{
              fontSize: 26,
              fontWeight: 700,
              color: "white",
              marginBottom: 8,
            }}
          >
            Plan Your Trip
          </h1>
          <p
            style={{
              fontSize: 13,
              color: "rgba(255,255,255,0.7)",
              lineHeight: 1.6,
              margin: 0,
            }}
          >
            Let AI craft the perfect Uttarakhand itinerary tailored to your
            preferences.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 600, margin: "0 auto", padding: "24px 16px" }}>
        {step < 9 && (
          <div style={{ marginBottom: 24 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 600, color: "#0a2a0a" }}>
                Step {step} of 8
              </span>
            </div>
            <div
              style={{
                height: 4,
                background: "#e5e7eb",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${(step / 8) * 100}%`,
                  height: "100%",
                  background: "#1D9E75",
                  transition: "width 0.3s",
                }}
              />
            </div>
          </div>
        )}

        {renderStepContent()}
      </div>
    </div>
  );
}
